import {
    IApplyToJobAction,
    ICreateJobApplicationCommentAction,
    IFetchActiveJobApplicationAction,
    ILoadNextOrPrevCandidateAction,
    ISaveForLaterAction,
    ISendChatMessageAction,
    ISetActiveJobApplicationAction,
    ISetTagAction,
    ITransferCandidateAction,
    ITransferCandidateModalData,
    IUpdateJobApplicationStatusAction,
    IUpdateUserRequirementScoreAction,
    IScheduleInterviewAction,
    IUpdateCandidateInfoAction,
    IBulkRejectApplicationModalData,
    IBulkRejectApplicationAction,
} from './interface';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IClientJobStatistics, IJobApplication } from '../../../common';
import { IDeleteTagAction } from '../..';

interface IJobApplicationSlice {
    jobIdSuccessfullyApplied: boolean;
    activeJobApplication: IJobApplication | null;
    jobApplicationsStatistics: IClientJobStatistics | null; // TODO: add type
    rejectApplicationModalData: IUpdateJobApplicationStatusAction | null;
    bulkRejectApplicationModalData: IBulkRejectApplicationModalData | null;
    transferCandidateModalData: ITransferCandidateModalData | null;
    uiState: {
        isApplyNowLoading: boolean;
        isActiveJobApplicationLoading: boolean;
        isUpdateJobApplicationStatusLoading: boolean;
        isFetchJobApplicationsStatisticsLoading: boolean;
        isCreateJobApplicationsCommentLoading: boolean;
        isSendChatMessageLoading: boolean;
        isRejectApplicationModalOpen: boolean;
        isBulkRejectApplicationModalOpen: boolean;
        isBulkRejectApplicationLoading: boolean;
        isTransferCandidateModalOpen: boolean;
        isTransferCandidateLoading: boolean;
        isSaveForLaterLoading: boolean;
        isApplyTagLoading: boolean;
        isDeleteTagLoading: boolean;
        isUpdateUserRequirementScoreLoading: boolean;
        isScheduleInterviewLoading: boolean;
        scheduleInterviewResult: 'success' | 'error' | null;
        isUpdateCandidateInfoLoading: boolean;
    };
}

const initialState: IJobApplicationSlice = {
    jobIdSuccessfullyApplied: false,
    activeJobApplication: null,
    jobApplicationsStatistics: null,
    rejectApplicationModalData: null,
    bulkRejectApplicationModalData: null,
    transferCandidateModalData: null,
    uiState: {
        isRejectApplicationModalOpen: false,
        isBulkRejectApplicationModalOpen: false,
        isBulkRejectApplicationLoading: false,
        isTransferCandidateModalOpen: false,
        isTransferCandidateLoading: false,
        isApplyNowLoading: false,
        isActiveJobApplicationLoading: false,
        isUpdateJobApplicationStatusLoading: false,
        isFetchJobApplicationsStatisticsLoading: false,
        isCreateJobApplicationsCommentLoading: false,
        isSendChatMessageLoading: false,
        isSaveForLaterLoading: false,
        isApplyTagLoading: false,
        isDeleteTagLoading: false,
        isUpdateUserRequirementScoreLoading: false,
        isScheduleInterviewLoading: false,
        scheduleInterviewResult: null,
        isUpdateCandidateInfoLoading: false,
    },
};

export const sliceName = 'jobApplication';
export const jobApplicationSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        applyToJob: (state, _action: PayloadAction<IApplyToJobAction>) => {
            state.uiState.isApplyNowLoading = true;
        },
        applyToJobSuccessful: (state) => {
            state.jobIdSuccessfullyApplied = true;
        },
        setJobIdSuccessfullyAppliedFalse: (state) => {
            state.jobIdSuccessfullyApplied = false;
        },
        applyToJobDone: (state) => {
            state.uiState.isApplyNowLoading = false;
        },
        fetchJobActiveApplication: (
            state,
            _action: PayloadAction<IFetchActiveJobApplicationAction>
        ) => {
            state.uiState.isActiveJobApplicationLoading = true;
        },
        updateJobApplicationStatus: (
            state,
            _action: PayloadAction<IUpdateJobApplicationStatusAction>
        ) => {
            state.uiState.isUpdateJobApplicationStatusLoading = true;
        },
        setActiveJobApplication: (
            state,
            action: PayloadAction<ISetActiveJobApplicationAction>
        ) => {
            state.activeJobApplication = action.payload.jobApplication;
            state.uiState.isActiveJobApplicationLoading = false;
        },
        fetchActiveJobApplicationFailed: (state) => {
            state.uiState.isActiveJobApplicationLoading = false;
        },
        updateJobApplicationStatusDone: (state) => {
            state.uiState.isUpdateJobApplicationStatusLoading = false;
        },
        fetchJobApplicationsStatistics: (state) => {
            state.uiState.isFetchJobApplicationsStatisticsLoading = true;
        },
        fetchJobApplicationsStatisticsDone: (state) => {
            state.uiState.isFetchJobApplicationsStatisticsLoading = false;
        },
        setJobApplicationsStatistics: (
            state,
            action: PayloadAction<IClientJobStatistics>
        ) => {
            state.jobApplicationsStatistics = action.payload;
            state.uiState.isFetchJobApplicationsStatisticsLoading = false;
        },
        createJobApplicationComment: (
            state,
            _action: PayloadAction<ICreateJobApplicationCommentAction>
        ) => {
            state.uiState.isCreateJobApplicationsCommentLoading = true;
        },
        createJobApplicationCommentDone: (state) => {
            state.uiState.isCreateJobApplicationsCommentLoading = false;
        },
        sendChatMessage: (
            state,
            _action: PayloadAction<ISendChatMessageAction>
        ) => {
            state.uiState.isSendChatMessageLoading = true;
        },
        sendChatMessageDone: (state) => {
            state.uiState.isSendChatMessageLoading = false;
        },
        saveForLater: (state, _action: PayloadAction<ISaveForLaterAction>) => {
            state.uiState.isSaveForLaterLoading = true;
        },
        saveForLaterDone: (state) => {
            state.uiState.isSaveForLaterLoading = false;
        },
        loadNextOrPrevCandidate: (
            _state,
            _action: PayloadAction<ILoadNextOrPrevCandidateAction>
        ) => {},
        setIsRejectApplicationModalOpen: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.uiState.isRejectApplicationModalOpen = action.payload;
        },
        setRejectApplicationModalData: (
            state,
            action: PayloadAction<IUpdateJobApplicationStatusAction>
        ) => {
            state.rejectApplicationModalData = action.payload;
        },
        setIsBulkRejectApplicationModalOpen: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.uiState.isBulkRejectApplicationModalOpen = action.payload;
        },
        setBulkRejectApplicationModalData: (
            state,
            action: PayloadAction<IBulkRejectApplicationModalData>
        ) => {
            state.bulkRejectApplicationModalData = action.payload;
        },
        bulkRejectApplication: (
            state,
            _action: PayloadAction<IBulkRejectApplicationAction>
        ) => {
            state.uiState.isBulkRejectApplicationLoading = true;
        },
        bulkRejectApplicationDone: (state) => {
            state.uiState.isBulkRejectApplicationLoading = false;
        },
        applyTagToJobApplication: (
            state,
            _action: PayloadAction<ISetTagAction>
        ) => {
            state.uiState.isApplyTagLoading = true;
        },
        applyTagToJobApplicationDone: (state) => {
            state.uiState.isApplyTagLoading = false;
        },
        deleteTagFromJobApplication: (
            state,
            _action: PayloadAction<IDeleteTagAction>
        ) => {
            state.uiState.isDeleteTagLoading = true;
        },
        deleteTagFromJobApplicationDone: (state) => {
            state.uiState.isDeleteTagLoading = false;
        },
        updateUserRequirementScore: (
            state,
            _action: PayloadAction<IUpdateUserRequirementScoreAction>
        ) => {
            state.uiState.isUpdateUserRequirementScoreLoading = true;
        },
        updateUserRequirementScoreDone: (state) => {
            state.uiState.isUpdateUserRequirementScoreLoading = false;
        },
        setIsTransferCandidateModalOpen: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.uiState.isTransferCandidateModalOpen = action.payload;
        },
        setTransferCandidateModalData: (
            state,
            action: PayloadAction<ITransferCandidateModalData>
        ) => {
            state.transferCandidateModalData = action.payload;
        },
        transferCandidate: (
            state,
            _action: PayloadAction<ITransferCandidateAction>
        ) => {
            state.uiState.isTransferCandidateLoading = true;
        },
        transferCandidateDone: (state) => {
            state.uiState.isTransferCandidateLoading = false;
        },
        scheduleInterview: (
            state,
            _action: PayloadAction<IScheduleInterviewAction>
        ) => {
            state.uiState.isScheduleInterviewLoading = true;
            state.uiState.scheduleInterviewResult = null;
        },
        scheduleInterviewSuccess: (state) => {
            state.uiState.isScheduleInterviewLoading = false;
            state.uiState.scheduleInterviewResult = 'success';
        },
        scheduleInterviewFailed: (state) => {
            state.uiState.isScheduleInterviewLoading = false;
            state.uiState.scheduleInterviewResult = 'error';
        },
        resetScheduleInterview: (state) => {
            state.uiState.isScheduleInterviewLoading = false;
            state.uiState.scheduleInterviewResult = null;
        },
        updateCandidateInfo: (
            state,
            _action: PayloadAction<IUpdateCandidateInfoAction>
        ) => {
            state.uiState.isUpdateCandidateInfoLoading = true;
        },
        updateCandidateInfoDone: (state) => {
            state.uiState.isUpdateCandidateInfoLoading = false;
        },
        updateCandidateInfoSuccess: (
            state,
            action: PayloadAction<{
                firstName: string;
                lastName: string;
                phone: string;
                email: string;
            }>
        ) => {
            if (state.activeJobApplication) {
                state.activeJobApplication.firstName = action.payload.firstName;
                state.activeJobApplication.lastName = action.payload.lastName;
                state.activeJobApplication.phone = action.payload.phone;
                state.activeJobApplication.email = action.payload.email;
            }
            state.uiState.isUpdateCandidateInfoLoading = false;
        },
    },
});

export const jobApplicationActions = jobApplicationSlice.actions;
export default jobApplicationSlice.reducer;
